import { AUTHENTICATION_METHOD, USER_ACTIVATION_METHOD } from '../../domain/domainCodes.js'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Boxes,
  ChevronDown,
  Copy,
  IdCard,
  LogIn,
  Pencil,
  Plug,
  RefreshCw,
  Server,
  UserPlus,
} from 'lucide-react'
import Checkbox from '../../components/Checkbox'
import Button from '../../components/Button'
import DataTable from '../../components/DataTable'
import IconButton from '../../components/IconButton'
import { useRemembered } from '../../components/listMemory'
import PageHeader from '../../components/PageHeader'
import PageLoader from '../../components/PageLoader'
import Select from '../../components/Select'
import StatusBadge from '../../components/StatusBadge'
import SectionNav from '../../components/SectionNav'
import useHashTab from '../../components/useHashTab'
import TextField from '../../components/TextField'
import SettingsCard, { Field, ToggleRow } from './SettingsCard'
import { useToast } from '../../components/toastContext'
import { getAdvanced, saveDataSource, saveSection, testDirectorySource } from './settingsService'
import { advancedStrings as S, common as C } from './strings'
import './settings.css'
import { serverText } from '../../lib/i18n'

/** Seven sections in two clusters: who gets in, and what the system talks to. */
const SECTION_GROUPS = [
  {
    items: [
      { id: 'login', label: S.tabs.login, icon: LogIn },
      { id: 'registration', label: S.tabs.registration, icon: UserPlus },
      { id: 'profile', label: S.tabs.profile, icon: IdCard },
    ],
  },
  {
    items: [
      { id: 'sources', label: S.tabs.sources, icon: Plug },
      { id: 'sync', label: S.tabs.sync, icon: RefreshCw },
      { id: 'servers', label: S.tabs.servers, icon: Server },
      { id: 'services', label: S.tabs.services, icon: Boxes },
    ],
  },
]

/** Every section this page answers to in its URL hash. */
const SECTION_IDS = SECTION_GROUPS.flatMap((group) => group.items.map((item) => item.id))

const ACTIVE_OPTIONS = [
  { value: 'true', label: C.active },
  { value: 'false', label: C.inactive },
]

const REGISTER_URL = '/Signup'
const pick = (settings, key, fallback = '') => settings?.[key] ?? fallback

/** إعدادات متقدمة — Views/ProfileMapping/Index.cshtml. */
export default function AdvancedSettingsPage() {
  const [tab, setTab] = useHashTab('login', SECTION_IDS)
  const [data, setData] = useState(null)
  const [form, setForm] = useState({})
  const toast = useToast()
  const [saving, setSaving] = useState('')
  const [sourceForm, setSourceForm] = useState(newDirectorySource())
  /* Which service group is open in the accordion. Remembered rather than plain state:
     the services panel is a long list of collapsed groups, and every trip away from the
     screen closed the one that was being worked on. */
  const [openService, setOpenService] = useRemembered('advanced-settings:service', '')

  useEffect(() => {
    getAdvanced().then((response) => {
      setData(response)
      setForm(initialForm(response))
    })
  }, [])

  const set = (key) => (value) => setForm((previous) => ({ ...previous, [key]: value }))
  const toggle = (key) => (checked) => setForm((previous) => ({ ...previous, [key]: checked }))

  /**
   * Each card posts on its own and reports its own outcome.
   *
   * `data` goes along as context: the profile-fields card edits two sets of names, but
   * the action it posts to replaces every field's three flags at once, so the body has to
   * be rebuilt from the loaded list rather than from the form alone.
   */
  const save = (section) => async () => {
    setSaving(section)
    const outcome = await saveSection(section, form, data)
    setSaving('')

    if (outcome.ok) {
      toast.success(serverText(outcome.message) || C.saved)
      return
    }
    if (outcome.nothingToSave) return
    if (outcome.unsupported) {
      toast.error(C.notAvailable)
      return
    }
    toast.error(outcome.message || C.saveFailed)
  }

  const reset = (section) => () =>
    setForm((previous) => ({ ...previous, ...sectionDefaults(section, data) }))

  /** Attribute1..5 wear the name configured in the same screen's أسماء بيانات card. */
  const fieldLabel = (field) => field.label ?? pick(data?.settings, field.setting, field.name)

  const toggleField = (key, name) =>
    setForm((previous) => {
      const current = previous[key] ?? []
      return {
        ...previous,
        [key]: current.includes(name)
          ? current.filter((item) => item !== name)
          : [...current, name],
      }
    })

  const editSource = useCallback((source) => setSourceForm({
    id: source.id,
    name: source.englishName || source.arabicName,
    type: source.type,
    connectionString: source.connectionString,
    username: source.username,
    password: '',
    domain: source.domain,
    filters: source.filters,
    ssl: source.ssl,
    isActive: source.isActive,
    limitAccessToGroup: source.limitAccessToGroup,
    adminGroups: source.adminGroups,
  }), [])

  const setSource = (key) => (value) =>
    setSourceForm((previous) => ({ ...previous, [key]: value }))

  const setSourceType = (type) =>
    setSourceForm((previous) => ({
      ...previous,
      type,
      connectionString:
        type === 'ENTRA'
          ? previous.connectionString || 'https://graph.microsoft.com/v1.0/'
          : previous.connectionString === 'https://graph.microsoft.com/v1.0/'
            ? ''
            : previous.connectionString,
      ssl: type === 'ENTRA' ? true : previous.ssl,
    }))

  const saveSource = async () => {
    setSaving('directorySource')
    const outcome = await saveDataSource(sourceForm)
    if (outcome.ok) {
      const refreshed = await getAdvanced()
      setData(refreshed)
      setForm(initialForm(refreshed))
      setSourceForm(newDirectorySource())
      toast.success(outcome.message || C.saved)
    } else toast.error(outcome.message || C.saveFailed)
    setSaving('')
  }

  const testSource = async () => {
    setSaving('testDirectorySource')
    const outcome = await testDirectorySource(sourceForm)
    setSaving('')
    if (outcome.ok) toast.success(outcome.message)
    else toast.error(outcome.message || C.saveFailed)
  }

  const sourceColumns = useMemo(
    () => [
      { key: 'type', header: S.sources.type, width: '140px' },
      {
        key: 'arabicName',
        header: S.sources.arabicName,
        render: (row) => <bdi>{row.arabicName}</bdi>,
      },
      {
        key: 'englishName',
        header: S.sources.englishName,
        render: (row) => <bdi>{row.englishName}</bdi>,
      },
      { key: 'username', header: S.sources.username },
      {
        key: 'connectionString',
        header: S.sources.connectionString,
        render: (row) => (
          <span className="set-mono" dir="ltr">
            {row.connectionString}
          </span>
        ),
      },
      {
        key: 'isActive',
        header: S.sources.isActive,
        width: '120px',
        value: (row) => (row.isActive ? C.active : C.inactive),
        render: (row) => (
          <StatusBadge tone={row.isActive ? 'success' : 'dark'}>
            {row.isActive ? C.active : C.inactive}
          </StatusBadge>
        ),
      },
      {
        key: 'actions',
        header: S.sources.edit,
        sortable: false,
        searchable: false,
        render: (row) => (
          <IconButton icon={Pencil} label={S.sources.edit} onClick={() => editSource(row)} />
        ),
      },
    ],
    [editSource],
  )

  const serverColumns = useMemo(
    () => [
      { key: 'id', header: S.servers.serverId, width: '160px' },
      {
        key: 'url',
        header: S.servers.serverUrl,
        render: (row) => (
          <span className="set-mono" dir="ltr">
            {row.url}
          </span>
        ),
      },
      // Same as the data sources above — no handler, so no column.
    ],
    [],
  )

  const groupOptions = useMemo(
    () => (data?.groups ?? []).map((group) => ({ value: group.id, label: group.name })),
    [data],
  )

  // Every field is controlled, so the form cannot render before its values exist —
  // React warns the moment an input flips from undefined to a value.
  if (!data) {
    return (
      <>
        <PageHeader title={S.title} />
        <PageLoader />
      </>
    )
  }

  return (
    <>
      <PageHeader title={S.title} />

      <div className="set-workspace">
        <SectionNav
          groups={SECTION_GROUPS}
          value={tab}
          onChange={setTab}
          ariaLabel={S.title}
        />

        <div className="set-panel">
      {tab === 'login' && (
        <>
          <SettingsCard
            title={S.login.general}
            hint={S.login.generalSubtitle}
            onSave={save('general')}
            onReset={reset('general')}
            saving={saving === 'general'}
          >
            {/* The legacy stacks these four in a single col-6. */}
            <div className="set-column">
              <Field label={S.login.licenseType}>
                {/* The licence payload is encrypted in dbo.License, so the type is not
                    readable from the dump — the رخصة الاستخدام screen shows the same. */}
                <p className="set-readonly">
                  <bdi>{form.licenseType || '—'}</bdi>
                </p>
              </Field>
              <Field label={S.login.authMethod}>
                {(id) => (
                  <Select
                    id={id}
                    value={form.authenticationId}
                    onChange={set('authenticationId')}
                    options={(data?.authMethods ?? []).map((method) => ({
                      value: method.id,
                      label: method.name,
                    }))}
                  />
                )}
              </Field>
              <Field label={S.login.authSource}>
                {(id) => (
                  <Select
                    id={id}
                    value={form.authenticationMode}
                    onChange={set('authenticationMode')}
                    options={[
                      { value: 'Forms', label: S.login.loginPage },
                      { value: 'Windows', label: S.login.ldapSso },
                    ]}
                  />
                )}
              </Field>
              <Field label={S.login.usernameFormat}>
                {(id) => (
                  <Select
                    id={id}
                    value={form.usernameFormat}
                    onChange={set('usernameFormat')}
                    options={S.login.usernameFormatOptions.map((option) => ({
                      value: option.value,
                      label: option.label,
                    }))}
                  />
                )}
              </Field>
            </div>
          </SettingsCard>

          <div className="set-grid">
            <SettingsCard
              title={S.login.otp}
              onSave={save('otp')}
              onReset={reset('otp')}
              saving={saving === 'otp'}
            >
              <ToggleRow
                id="OTPEnabled"
                checked={!!form.otpEnabled}
                onChange={toggle('otpEnabled')}
                label={S.login.otpEnabled}
              />
              <Field label={S.login.otpTarget}>
                {(id) => (
                  <Select
                    id={id}
                    value={form.otpTarget}
                    onChange={set('otpTarget')}
                    options={S.login.otpTargets}
                  />
                )}
              </Field>
              <Field label={S.login.otpLength}>
                {(id) => (
                  <TextField id={id} value={form.otpLength} onChange={set('otpLength')} dir="ltr" />
                )}
              </Field>
              <Field label={S.login.otpValidPeriod}>
                {(id) => (
                  <TextField
                    id={id}
                    value={form.otpValidPeriod}
                    onChange={set('otpValidPeriod')}
                    dir="ltr"
                  />
                )}
              </Field>
            </SettingsCard>

            <SettingsCard
              title={S.login.lockout}
              onSave={save('lockout')}
              onReset={reset('lockout')}
              saving={saving === 'lockout'}
            >
              <ToggleRow
                id="EnableAccountLockout"
                checked={!!form.lockoutEnabled}
                onChange={toggle('lockoutEnabled')}
                label={S.login.lockoutEnabled}
              />
              <Field label={S.login.attempts}>
                {(id) => (
                  <TextField id={id} value={form.attempts} onChange={set('attempts')} dir="ltr" />
                )}
              </Field>
              <Field label={S.login.lockoutWindow}>
                {(id) => (
                  <TextField
                    id={id}
                    value={form.lockoutWindow}
                    onChange={set('lockoutWindow')}
                    dir="ltr"
                  />
                )}
              </Field>
              <Field label={S.login.sessionTimeout}>
                {(id) => (
                  <TextField
                    id={id}
                    value={form.sessionTimeout}
                    onChange={set('sessionTimeout')}
                    dir="ltr"
                  />
                )}
              </Field>
            </SettingsCard>
          </div>

          <SettingsCard
            title={S.login.terms}
            onSave={save('terms')}
            saveLabel={S.login.termsSubmit}
            saving={saving === 'terms'}
          >
            <div className="set-half">
              <div>
                <Field label={S.login.termsAr}>
                  {(id) => (
                    <textarea
                      id={id}
                      className="set-textarea"
                      rows={12}
                      value={form.termsAr}
                      onChange={(event) => set('termsAr')(event.target.value)}
                    />
                  )}
                </Field>
                {/* The legacy pairs a bold label and a note with a bare switch. */}
                <Field label={S.login.termsEnable} note={S.login.termsEnableNote}>
                  {(id) => (
                    <Checkbox
                      id={id}
                      checked={!!form.termsEnabled}
                      onChange={toggle('termsEnabled')}
                    />
                  )}
                </Field>
              </div>
              <div>
                <Field label={S.login.termsEn}>
                  {(id) => (
                    <textarea
                      id={id}
                      className="set-textarea"
                      dir="ltr"
                      rows={12}
                      value={form.termsEn}
                      onChange={(event) => set('termsEn')(event.target.value)}
                    />
                  )}
                </Field>
                <Field label={S.login.termsUpdated} note={S.login.termsUpdatedNote}>
                  {(id) => (
                    <Checkbox
                      id={id}
                      checked={!!form.termsUpdated}
                      onChange={toggle('termsUpdated')}
                    />
                  )}
                </Field>
              </div>
            </div>
          </SettingsCard>
        </>
      )}

      {tab === 'profile' && (
        <>
          <SettingsCard
            title={S.profile.title}
            onSave={save('profileFields')}
            saveLabel={C.update}
            saving={saving === 'profileFields'}
            actions={
              <div className="set-company">
                <span className="set-company__label">
                  <bdi>{S.profile.chooseCompany}</bdi>
                </span>
                <Select
                  value={form.companyId}
                  onChange={set('companyId')}
                  options={[
                    { value: data?.company?.id ?? 1, label: data?.company?.arabicName ?? '' },
                  ]}
                />
              </div>
            }
          >
            <p className="set-heading">
              <bdi>{S.profile.fieldsHeading}</bdi>
            </p>
            <div className="set-fields">
              {S.profile.fields.map((field, index) => (
                <div key={field.name} className="set-fields__row">
                  <ToggleRow
                    id={`visibleField_${field.name}`}
                    checked={(form.visibleFields ?? []).includes(field.name)}
                    onChange={() => toggleField('visibleFields', field.name)}
                    label={fieldLabel(field)}
                  />
                  <Checkbox
                    id={`editField_${field.name}`}
                    checked={(form.editableFields ?? []).includes(field.name)}
                    disabled={index === S.profile.lockedEditIndex}
                    onChange={() => toggleField('editableFields', field.name)}
                  >
                    <bdi>{S.profile.allowEdit}</bdi>
                  </Checkbox>
                </div>
              ))}
            </div>
          </SettingsCard>

          <SettingsCard
            title={S.profile.customAttributes}
            onSave={save('customAttributes')}
            saveLabel={C.update}
            saving={saving === 'customAttributes'}
          >
            {[1, 2, 3, 4, 5].map((index) => (
              <Field key={index} label={`${S.profile.attribute} ${index}`}>
                <div className="set-pair">
                  <TextField
                    value={form[`attribute${index}Ar`]}
                    onChange={set(`attribute${index}Ar`)}
                    placeholder={S.profile.arabicName}
                  />
                  <TextField
                    value={form[`attribute${index}En`]}
                    onChange={set(`attribute${index}En`)}
                    placeholder={S.profile.englishName}
                    dir="ltr"
                  />
                </div>
              </Field>
            ))}
          </SettingsCard>

          <SettingsCard
            title={S.profile.otherProperties}
            onSave={save('otherProperties')}
            saveLabel={C.update}
            saving={saving === 'otherProperties'}
          >
            <Field label={S.profile.allowEditPageTitle}>
              {(id) => (
                <ToggleRow
                  id={id}
                  checked={!!form.allowProfileEditPage}
                  onChange={toggle('allowProfileEditPage')}
                  label={S.profile.allowEditPage}
                />
              )}
            </Field>
            <Field label={S.profile.forceGroupLabel}>
              <ToggleRow
                id="chkbxForceGroupChange"
                checked={!!form.forceGroupChange}
                onChange={toggle('forceGroupChange')}
                label={S.profile.forceGroupCheck}
              />
              <div className="set-inline">
                <span className="set-inline__label">
                  <bdi>{S.profile.forceGroupSelect}</bdi>
                </span>
                <Select
                  value={form.forceGroupId}
                  onChange={set('forceGroupId')}
                  options={groupOptions}
                />
              </div>
            </Field>
          </SettingsCard>
        </>
      )}

      {tab === 'sources' && (
        <>
          <SettingsCard title={sourceForm.id ? S.sources.edit : S.sources.newSource}>
            <div className="set-half">
              <div>
                <Field label={S.sources.type}>
                  {(id) => <Select id={id} value={sourceForm.type} onChange={setSourceType} options={[
                    { value: 'LDAP', label: 'LDAP / LDAPS' },
                    { value: 'ENTRA', label: 'Microsoft Entra ID' },
                  ]} />}
                </Field>
                <Field label={S.sources.name}>
                  {(id) => <TextField id={id} value={sourceForm.name} onChange={setSource('name')} />}
                </Field>
                <Field label={sourceForm.type === 'ENTRA' ? S.sources.tenantId : S.sources.domain}>
                  {(id) => <TextField id={id} value={sourceForm.domain} onChange={setSource('domain')} dir="ltr" />}
                </Field>
                <Field label={sourceForm.type === 'ENTRA' ? S.sources.clientId : S.sources.username}>
                  {(id) => <TextField id={id} value={sourceForm.username} onChange={setSource('username')} dir="ltr" />}
                </Field>
                <Field label={sourceForm.type === 'ENTRA' ? S.sources.clientSecret : S.sources.password} note={sourceForm.id ? S.sources.secretUnchanged : ''}>
                  {(id) => <TextField id={id} type="password" value={sourceForm.password} onChange={setSource('password')} dir="ltr" />}
                </Field>
              </div>
              <div>
                <Field label={sourceForm.type === 'ENTRA' ? S.sources.graphEndpoint : S.sources.connectionString}>
                  {(id) => <TextField id={id} value={sourceForm.connectionString} onChange={setSource('connectionString')} dir="ltr" />}
                </Field>
                <Field label={sourceForm.type === 'ENTRA' ? S.sources.odataFilter : S.sources.ldapFilter}>
                  {(id) => <TextField id={id} value={sourceForm.filters} onChange={setSource('filters')} dir="ltr" />}
                </Field>
                <Field label={S.sources.accessGroup}>
                  {(id) => <TextField id={id} value={sourceForm.limitAccessToGroup} onChange={setSource('limitAccessToGroup')} dir="ltr" />}
                </Field>
                <Field label={S.sources.adminGroups} note={S.sources.groupsNote}>
                  {(id) => <TextField id={id} value={sourceForm.adminGroups} onChange={setSource('adminGroups')} dir="ltr" />}
                </Field>
                {sourceForm.type === 'ENTRA' && (
                  <p className="set-readonly"><bdi>{S.sources.entraPrerequisites}</bdi></p>
                )}
                {sourceForm.type === 'LDAP' && <ToggleRow id="directorySsl" checked={sourceForm.ssl} onChange={setSource('ssl')} label={S.sources.ssl} />}
                <ToggleRow id="directoryActive" checked={sourceForm.isActive} onChange={setSource('isActive')} label={S.sources.isActive} />
              </div>
            </div>
            <div className="set-actions">
              <Button variant="soft" onClick={testSource} disabled={saving !== ''}>{S.sources.test}</Button>
              <Button onClick={saveSource} disabled={saving !== ''}>{C.save}</Button>
              <Button variant="quiet" onClick={() => setSourceForm(newDirectorySource())}>{C.reset}</Button>
            </div>
          </SettingsCard>
          <DataTable
            columns={sourceColumns}
            rows={data?.dataSources ?? []}
            loading={!data}
            getRowId={(row) => row.id}
          />
        </>
      )}

      {tab === 'sync' && (
        <>
          <SettingsCard
            title={S.sync.title}
            onSave={save('integration')}
            saving={saving === 'integration'}
          >
            <div className="set-half">
              <div>
                <Field label={S.sync.deptsSource}>
                  {(id) => (
                    <Select
                      id={id}
                      value={form.deptsSource}
                      onChange={set('deptsSource')}
                      options={directorySourceOptions(data)}
                    />
                  )}
                </Field>
                <Field label={S.sync.usersSource}>
                  {(id) => (
                    <Select
                      id={id}
                      value={form.usersSource}
                      onChange={set('usersSource')}
                      options={directorySourceOptions(data)}
                    />
                  )}
                </Field>
                <Field label={S.sync.userFetchSource}>
                  {(id) => (
                    <Select
                      id={id}
                      value={form.userFetchSource}
                      onChange={set('userFetchSource')}
                      options={directorySourceOptions(data)}
                    />
                  )}
                </Field>
              </div>
              <div>
                {/* The legacy leaves only ImportSync selectable for departments and only
                    Sync for the per-user fetch; the other options are commented out. */}
                <Field label={S.sync.deptsType}>
                  {(id) => (
                    <Select
                      id={id}
                      value={form.deptsType}
                      onChange={set('deptsType')}
                      options={[{ value: 'ImportSync', label: S.sync.importSync }]}
                    />
                  )}
                </Field>
                <Field label={S.sync.usersType}>
                  {(id) => (
                    <Select
                      id={id}
                      value={form.usersType}
                      onChange={set('usersType')}
                      options={[
                        { value: 'Import', label: S.sync.import },
                        { value: 'Sync', label: S.sync.syncOnly },
                        { value: 'ImportSync', label: S.sync.importSync },
                      ]}
                    />
                  )}
                </Field>
                <Field label={S.sync.userFetchType}>
                  {(id) => (
                    <Select
                      id={id}
                      value={form.userFetchType}
                      onChange={set('userFetchType')}
                      options={[{ value: 'Sync', label: S.sync.syncOnly }]}
                    />
                  )}
                </Field>
              </div>
            </div>
          </SettingsCard>

          <SettingsCard
            title={S.sync.autoTitle}
            onSave={save('autoIntegration')}
            saving={saving === 'autoIntegration'}
          >
            <div className="set-half">
              <div>
                <Field label={S.sync.deptsAutoFetch}>
                  <div className="set-pair set-pair--status">
                    <Select
                      value={form.deptsAutoFetch}
                      onChange={set('deptsAutoFetch')}
                      options={ACTIVE_OPTIONS}
                    />
                    <TextField
                      value={form.deptsAutoFetchInterval}
                      onChange={set('deptsAutoFetchInterval')}
                      placeholder={S.sync.delay}
                      dir="ltr"
                    />
                  </div>
                </Field>
                <Field label={S.sync.overLicense}>
                  {(id) => (
                    <Select
                      id={id}
                      value={form.overLicense}
                      onChange={set('overLicense')}
                      options={S.sync.overLicenseOptions}
                    />
                  )}
                </Field>
                <ToggleRow
                  id="DeleteADGroupsWithNoUsers"
                  checked={!!form.deleteEmptyGroups}
                  onChange={toggle('deleteEmptyGroups')}
                  label={S.sync.deleteEmptyGroups}
                />
              </div>
              <div>
                <Field label={S.sync.usersAutoFetch}>
                  <div className="set-pair set-pair--status">
                    <Select
                      value={form.usersAutoFetch}
                      onChange={set('usersAutoFetch')}
                      options={ACTIVE_OPTIONS}
                    />
                    <TextField
                      value={form.usersAutoFetchInterval}
                      onChange={set('usersAutoFetchInterval')}
                      placeholder={S.sync.delay}
                      dir="ltr"
                    />
                  </div>
                </Field>
                <Field label={S.sync.commandMode}>
                  {(id) => (
                    <Select
                      id={id}
                      value={form.commandMode}
                      onChange={set('commandMode')}
                      options={S.sync.commandModeOptions}
                    />
                  )}
                </Field>
              </div>
            </div>
          </SettingsCard>
        </>
      )}

      {tab === 'registration' && (
        <SettingsCard
          title={S.registration.title}
          onSave={save('registration')}
          onReset={reset('registration')}
          saving={saving === 'registration'}
        >
          <ToggleRow
            id="ActivateInvitations"
            checked={!!form.activateInvitations}
            onChange={toggle('activateInvitations')}
            label={S.registration.activateInvitations}
          />
          <ToggleRow
            id="ShowRegistrationLinkOnLoginPage"
            checked={!!form.showRegistrationLink}
            onChange={toggle('showRegistrationLink')}
            label={S.registration.showRegistrationLink}
          />
          <ToggleRow
            id="ActivateResetPasswordPage"
            checked={!!form.activateResetPassword}
            onChange={toggle('activateResetPassword')}
            label={S.registration.activateResetPassword}
          />

          <fieldset className="set-fieldset" disabled={!form.showRegistrationLink}>
            <Field label={S.registration.activationMethod}>
              {(id) => (
                <Select
                  id={id}
                  value={form.activationBy}
                  onChange={set('activationBy')}
                  options={S.registration.activationOptions}
                />
              )}
            </Field>
            <Field label={S.registration.emailOnDomain}>
              {(id) => (
                <Checkbox
                  id={id}
                  checked={!!form.emailOnDomain}
                  onChange={toggle('emailOnDomain')}
                />
              )}
            </Field>
            <Field label={S.registration.domains}>
              {(id) => (
                <TextField
                  id={id}
                  value={form.companyDomains}
                  onChange={set('companyDomains')}
                  placeholder={S.registration.domains}
                  dir="ltr"
                />
              )}
            </Field>
          </fieldset>

          <Field label={S.registration.copyLink}>
            <div className="set-inline">
              <IconButton
                icon={Copy}
                label={S.registration.copyLink}
                onClick={() => navigator.clipboard?.writeText(REGISTER_URL)}
              />
              <a className="set-link" href={REGISTER_URL}>
                <bdi>{S.registration.registerLink}</bdi>
              </a>
            </div>
          </Field>

          <div className="set-note">
            <h4>
              <bdi>{S.registration.noteTitle}</bdi>
            </h4>
            <p>
              <bdi>{S.registration.noteBody}</bdi>
            </p>
          </div>
        </SettingsCard>
      )}

      {tab === 'servers' && (
        <DataTable
          /* A second list on the same screen: the address already belongs to the first. */
          pageParam={null}
          columns={serverColumns}
          rows={data?.servers ?? []}
          loading={!data}
          getRowId={(row) => row.id}
          // Same again — no form behind it yet.
        />
      )}

      {tab === 'services' && (
        <SettingsCard
          title={S.services.title}
          onSave={save('services')}
          onReset={reset('services')}
          saving={saving === 'services'}
        >
          {S.services.groups.map((group) => {
            const expanded = openService === group.id
            return (
              <div key={group.id} className="set-accordion">
                <button
                  type="button"
                  className="set-accordion__head"
                  aria-expanded={expanded}
                  onClick={() => setOpenService(expanded ? '' : group.id)}
                >
                  <ChevronDown
                    className={`set-accordion__caret${expanded ? ' set-accordion__caret--open' : ''}`}
                    size={18}
                    strokeWidth={1.75}
                    aria-hidden="true"
                  />
                  <bdi>{S.services[group.id]}</bdi>
                </button>

                {/* One wrapper inside the collapsing grid — `grid-template-rows: 0fr`
                    only sizes the first row, so several children would stay visible. */}
                <div
                  className={`set-accordion__body${expanded ? ' set-accordion__body--open' : ''}`}
                  {...(expanded ? null : { inert: true })}
                >
                  <div className="set-accordion__inner">
                  {group.id === 'notifications' && (
                    <>
                      <h4 className="set-services__title">
                        <bdi>{S.services.emailSettings}</bdi>
                      </h4>
                      <div className="set-thirds">
                        <Field
                          label={S.services.enableNotificationService}
                          hint={S.services.enableNotificationServiceHint}
                        >
                          {(id) => (
                            <Select
                              id={id}
                              value={form.ServiceEnabledNotifications}
                              onChange={set('ServiceEnabledNotifications')}
                              options={ACTIVE_OPTIONS}
                            />
                          )}
                        </Field>
                        <Field label={S.services.maxRetries} hint={S.services.maxRetriesHint}>
                          {(id) => (
                            <TextField
                              id={id}
                              value={form.MaxRetries}
                              onChange={set('MaxRetries')}
                              placeholder="3"
                              dir="ltr"
                            />
                          )}
                        </Field>
                        <Field
                          label={S.services.maxTaskMinutes}
                          hint={S.services.maxTaskMinutesHint}
                        >
                          {(id) => (
                            <TextField
                              id={id}
                              value={form.MaxTaskExecutionMinutes}
                              onChange={set('MaxTaskExecutionMinutes')}
                              placeholder="10"
                              dir="ltr"
                            />
                          )}
                        </Field>
                      </div>
                    </>
                  )}

                  {group.items.map((service) => (
                    <div key={service.id} className="set-services__item">
                      <h4 className="set-services__title">
                        <bdi>{service.name}</bdi>
                      </h4>
                      <div className="set-thirds">
                        <Field
                          label={C.status}
                          hint={
                            group.id === 'notifications'
                              ? S.services.statusHintNotifications
                              : S.services.statusHint
                          }
                        >
                          {(id) => (
                            <Select
                              id={id}
                              value={form[`ServiceEnabled${service.id}`]}
                              onChange={set(`ServiceEnabled${service.id}`)}
                              options={ACTIVE_OPTIONS}
                            />
                          )}
                        </Field>
                        <Field label={S.sync.delay} hint={S.services.delayHint}>
                          {(id) => (
                            <TextField
                              id={id}
                              value={form[`ServiceDelay${service.id}`]}
                              onChange={set(`ServiceDelay${service.id}`)}
                              placeholder="00:00:10"
                              dir="ltr"
                            />
                          )}
                        </Field>
                        <Field label={S.services.logs} hint={S.services.logsHint}>
                          {(id) => (
                            <Select
                              id={id}
                              value={form[`ServiceLogs${service.id}`]}
                              onChange={set(`ServiceLogs${service.id}`)}
                              options={ACTIVE_OPTIONS}
                            />
                          )}
                        </Field>
                        {service.maxProcessed && (
                          <Field
                            label={S.services.maxProcessed}
                            hint={S.services.maxProcessedHint}
                          >
                            {(id) => (
                              <TextField
                                id={id}
                                value={form[`ServiceMaxProcessedEntries${service.id}`]}
                                onChange={set(`ServiceMaxProcessedEntries${service.id}`)}
                                placeholder="30"
                                dir="ltr"
                              />
                            )}
                          </Field>
                        )}
                        {service.maxParallelism && (
                          <Field
                            label={S.services.maxParallelism}
                            hint={S.services.maxParallelismHint}
                          >
                            {(id) => (
                              <TextField
                                id={id}
                                value={form[`ServiceMaxDegreeOfParallelism${service.id}`]}
                                onChange={set(`ServiceMaxDegreeOfParallelism${service.id}`)}
                                placeholder="4"
                                dir="ltr"
                              />
                            )}
                          </Field>
                        )}
                      </div>
                    </div>
                  ))}
                  </div>
                </div>
              </div>
            )
          })}
        </SettingsCard>
      )}
        </div>
      </div>
    </>
  )
}

function newDirectorySource() {
  return {
    id: 0,
    name: '',
    type: 'ENTRA',
    connectionString: 'https://graph.microsoft.com/v1.0/',
    username: '',
    password: '',
    domain: '',
    filters: '',
    ssl: true,
    isActive: true,
    limitAccessToGroup: '',
    adminGroups: '',
  }
}

function directorySourceOptions(data) {
  return [
    { value: 'None', label: C.none },
    ...(data?.dataSources ?? [])
      .filter((source) => source.isActive)
      .map((source) => ({
        value: String(source.id),
        label: `${source.englishName || source.arabicName} (${source.type})`,
      })),
  ]
}

function initialForm(data) {
  return {
    ...sectionDefaults('general', data),
    ...sectionDefaults('otp', data),
    ...sectionDefaults('lockout', data),
    ...sectionDefaults('terms', data),
    ...sectionDefaults('profileFields', data),
    ...sectionDefaults('customAttributes', data),
    ...sectionDefaults('otherProperties', data),
    ...sectionDefaults('integration', data),
    ...sectionDefaults('autoIntegration', data),
    ...sectionDefaults('registration', data),
    ...sectionDefaults('services', data),
  }
}

function sectionDefaults(section, data) {
  const s = data?.settings ?? {}
  const company = data?.company ?? {}

  switch (section) {
    case 'general':
      return {
        licenseType: pick(s, 'LicenseType'),
        companyId: company.id ?? 1,
        authenticationId: company.authenticationId ?? AUTHENTICATION_METHOD.LOCAL,
        authenticationMode: pick(s, 'AuthenticationMode', 'Forms'),
        usernameFormat: pick(s, 'UsernameFormatOptions', '1'),
      }
    case 'otp':
      return {
        otpEnabled: pick(s, 'OTPEnabled') === 'true',
        otpTarget: pick(s, 'OTPTarget', 'Admins'),
        otpLength: pick(s, 'OTPLength', '4'),
        otpValidPeriod: pick(s, 'OTPValidPeriod', '120'),
      }
    case 'lockout':
      return {
        lockoutEnabled: pick(s, 'EnableAccountLockout') === 'true',
        attempts: pick(s, 'AttemptsBeforeLockout', '5'),
        lockoutWindow: pick(s, 'AccountLockoutWindow', '5'),
        sessionTimeout: pick(s, 'SessionTimeoutInMinutes', '600'),
      }
    case 'terms':
      /* Read from what the server sent, not blanks: the card edits the document every
         user has to accept, and starting it empty is how one gets erased. */
      return {
        termsAr: data?.terms?.arabic ?? '',
        termsEn: data?.terms?.english ?? '',
        termsEnabled: Boolean(data?.terms?.isEnabled),
        /* Always starts off. Ticking it forces every user who has already accepted to
           accept again, so it is a decision made per save, never a remembered state. */
        termsUpdated: false,
      }
    case 'profileFields':
      return {
        visibleFields: (data?.profileFields ?? [])
          .filter((field) => field.isVisible)
          .map((field) => field.name),
        editableFields: (data?.profileFields ?? [])
          .filter((field) => field.isEditable)
          .map((field) => field.name),
      }
    case 'customAttributes':
      return Object.fromEntries(
        [1, 2, 3, 4, 5].flatMap((index) => [
          [`attribute${index}Ar`, pick(s, `Attribute${index}_AR_Name`, `Attribute${index}`)],
          [`attribute${index}En`, pick(s, `Attribute${index}_EN_Name`, `Attribute${index}`)],
        ]),
      )
    case 'otherProperties':
      return {
        allowProfileEditPage: true,
        forceGroupChange: false,
        forceGroupId: data?.groups?.[0]?.id ?? '',
      }
    case 'integration':
      return {
        deptsSource: pick(s, 'DepartmentsIntegrationDataSource', 'None'),
        usersSource: pick(s, 'UsersIntegrationDataSource', 'None'),
        userFetchSource: pick(s, 'UserFetchInfoDataSource', 'None'),
        deptsType: 'ImportSync',
        usersType: pick(s, 'UsersIntegrationType', 'Sync'),
        userFetchType: 'Sync',
      }
    case 'autoIntegration':
      return {
        deptsAutoFetch: pick(s, 'DeptsAutoFetch', 'false'),
        deptsAutoFetchInterval: pick(s, 'DeptsAutoFetchInterval', '180'),
        usersAutoFetch: pick(s, 'UsersAutoFetch', 'false'),
        usersAutoFetchInterval: pick(s, 'UsersAutoFetchInterval', '1200'),
        overLicense: pick(s, 'WhenFetchedUsersCountGreaterThanLicense', 'Reject'),
        deleteEmptyGroups: pick(s, 'DeleteADGroupsWithNoUsers') === 'true',
        commandMode: pick(s, 'SyncProfilesCommandMode', 'bulkSql'),
      }
    case 'registration':
      return {
        activateInvitations: pick(s, 'ActivateInvitations') === 'true',
        showRegistrationLink: pick(s, 'ShowRegistrationLinkOnLoginPage') === 'true',
        activateResetPassword: pick(s, 'ActivateResetPasswordPage') === 'true',
        activationBy: company.activateByAdmin ? String(USER_ACTIVATION_METHOD.ADMIN) : String(USER_ACTIVATION_METHOD.EMAIL),
        emailOnDomain: !!company.userEmailOnDomain,
        companyDomains: company.companyDomains ?? '',
      }
    case 'services':
      return {
        ServiceEnabledNotifications: pick(s, 'Email_ServiceState', 'true'),
        MaxRetries: pick(s, 'MaxRetries', '3'),
        MaxTaskExecutionMinutes: pick(s, 'MaxTaskExecutionMinutes', '10'),
        ...Object.fromEntries(
          S.services.groups
            .flatMap((group) => group.items)
            .flatMap((service) => [
              [`ServiceEnabled${service.id}`, pick(s, `ServiceEnabled${service.id}`, 'false')],
              [`ServiceDelay${service.id}`, pick(s, `ServiceDelay${service.id}`, '00:00:10')],
              [`ServiceLogs${service.id}`, pick(s, `ServiceLogs${service.id}`, 'true')],
              [
                `ServiceMaxProcessedEntries${service.id}`,
                pick(s, `ServiceMaxProcessedEntries${service.id}`, ''),
              ],
              [
                `ServiceMaxDegreeOfParallelism${service.id}`,
                pick(s, `ServiceMaxDegreeOfParallelism${service.id}`, ''),
              ],
            ]),
        ),
      }
    default:
      return {}
  }
}
